/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Fragment, useState, useEffect, useCallback, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { useSelector, useDispatch } from 'react-redux';
import { getCurrency } from '@fh-components/fh-js-api-core/store/slices/currencySlice';
import getProductWithoutQuantity from '@/lib/getProductWithoutQuantity';
import {
	createInCart,
	updateCartIds,
	updateCartCount,
} from '@fh-components/fh-js-api-core/store/slices/cartSlice';
import { cartInfoSelector, wishlistIdsSelector } from '@/store';
import { createSession } from '@fh-components/fh-js-api-core/store/slices/sessionSlice';
import Link from 'next/link';
import clsx from 'clsx';
import {
	createInWishlist,
	updateWishlistIds,
} from '@fh-components/fh-js-api-core/store/slices/wishlistSlice';
import { Picture, Loader, WishlistBtn } from '@/components';
import ecommerce from '@/seo/Ecommerce';
import getLinkProps from '@/lib/getLinkProps';
import Cookie from 'js-cookie';
import { setPageProductData } from '@/seo/PopMechanicData';
import ProductOptions from './ProductOptions';

import styles from './ProductConfig.module.scss';

const AddToCart = dynamic(() => import('../addToCart'), {
	loading: () => <Loader />,
});

const SizeModal = dynamic(() => import('../sizeModal'), {
	loading: () => <Loader />,
});

const ProductOptionsSliderModal = ({ options, setCurrentSlug, isLook, onLoadingError }) => {
	const handleClick = slug => {
		if (!isLook) {
			setCurrentSlug(slug, true);
		}
	};

	return (
		<div className={styles.slider}>
			{options
				.filter(({ active }) => (isLook ? active : true))
				.map(({ slug, media, has: hasSelectedConfig, active }) => {
					return (
						<div
							className={styles.sliderItem}
							role='button'
							tabIndex={0}
							key={slug}
							onClick={() => handleClick(slug)}
						>
							<a
								className={clsx(
									{
										[styles.styledInactive]: !hasSelectedConfig,
										[styles.active]: active,
									},
									styles.option,
									styles.styledImage,
								)}
							>
								{media && (
									<Picture
										{...media}
										onLoadError={onLoadingError}
									/>
								)}
							</a>
						</div>
					);
				})}
		</div>
	);
};

const ProductOptionsSlider = ({ options, setCurrentSlug, isLook, onLoadingError }) => {
	const handleClick = slug => {
		if (!isLook) {
			setCurrentSlug(slug, true);
		}
	};

	return (
		<div className={styles.slider}>
			{options.map(({ media, has: hasSelectedConfig, active, model }) => {
				const [href, as] = getLinkProps({ type: 'product', value: model?.slug ?? '' });
				const Wrapper = active ? Fragment : Link;

				return (
					<div
						className={styles.sliderItem}
						key={model.slug}
						onClick={!active ? () => handleClick(model.slug) : null}
					>
						<Wrapper {...(active ? null : { href, as, passHref: true, scroll: false })}>
							<a
								className={clsx(
									{
										[styles.styledInactive]: !hasSelectedConfig,
										[styles.active]: active,
									},
									styles.option,
									styles.styledImage,
								)}
							>
								{media && (
									<Picture
										{...media}
										onLoadError={onLoadingError}
									/>
								)}
							</a>
						</Wrapper>
					</div>
				);
			})}
		</div>
	);
};

const ProductConfig = ({
	switchAttributes,
	sizeChoosenModalState = [],
	slug,
	currentSlug,
	setCurrentSlug,
	setSizeLookItem = () => null,
	setActiveImageName = () => null,
	setColorState = null,
	inStock = true,
	data,
	isModal,
	isLook,
	noBucketButton = false,
	noShowSizeTable = false,
	isProductPage,
	children,
	index,
	productInfo = {},
	setActualPrice,
	actualPrice,
	modelId,
	onLoadingError,
}) => {
	const currency = useSelector(getCurrency);
	const { ids, count } = useSelector(cartInfoSelector);
	const [sizeChosenModal, setSizeChosenModal] = useState(false);
	const [currentSize, setCurrentSize] = useState(true);
	const [isOpenSize, setIsOpenSize] = useState(false);

	const dispatch = useDispatch();
	const wishlistIds = useSelector(wishlistIdsSelector);

	const productId =
		switchAttributes.length > 0 &&
		switchAttributes.some(attr => attr.name === 'Размер производителя')
			? currentSize?.value
			: currentSlug;
	const productIdForCartBtn = isModal && !sizeChosenModal ? null : productId;

	useEffect(() => {
		setPageProductData(data);
	}, [data]);

	const addToCart = useCallback(
		newValue => {
			// eslint-disable-next-line no-shadow
			const changeProductDataSize = (data, size) => {
				if (!size) return data;
				try {
					let attributes = [];

					attributes.push({ value: size.label });
					for (let i = 0; i < data.attributes.length; i++) {
						const attribute = data.attributes[i];

						if (attribute.name === 'Основной цвет') {
							const value = attribute.options[0].name;

							attributes.push({ value });
							break;
						}
					}

					return {
						...data,
						external_id: size.value,
						switchable_attributes: attributes,
					};
				} catch (error) {
					console.warn('size value was not sent correctly');
					return data;
				}
			};

			const newData = changeProductDataSize(data, currentSize || newValue);

			ecommerce.viewItemList(
				[{ ...newData, quantity: 1 }],
				currency,
				productInfo?.pageName ?? 'Страница товара',
				'add_to_cart',
				productInfo?.indexCard ?? 1,
			);
		},
		[currency, currentSize, data],
	);

	const putIntoCart = newValue => {
		const mindboxDeviceUUID = Cookie.get('mindboxDeviceUUID');
		const isInCart = ids.includes(newValue.value);

		if (!isInCart) {
			dispatch(updateCartIds({ ids: [...ids, newValue.value] }));
			dispatch(updateCartCount({ count: count + 1 }));
			dispatch(
				createInCart({
					mindboxDeviceUUID: mindboxDeviceUUID || '',
					id: newValue.value,
					meta: productInfo,
					quantity: 1,
					onSuccess: () => {
						if (window && window.generalPixel) {
							window.generalPixel.postClick('a1');
						}
						addToCart(newValue);
					},
					onError: () => {
						dispatch(createSession(true, undefined, { forceRefresh: true }));
					},
				}),
			);
		}
	};

	const putIntoWishlist = id => {
		const mindboxDeviceUUID = Cookie.get('mindboxDeviceUUID');
		if (!wishlistIds.includes(id)) {
			dispatch(
				createInWishlist({
					id: id,
					mindboxDeviceUUID: mindboxDeviceUUID || '',
					onSuccess: () => {
						dispatch(
							updateWishlistIds({
								ids: [
									...wishlistIds,
									{
										product_id: id,
										model_id: modelId,
									},
								],
							}),
						);
						ecommerce.viewItemList(
							getProductWithoutQuantity([data]),
							currency,
							productInfo?.pageName ?? 'Страница товара',
							'add_to_wishlist',
							productInfo?.indexCard ?? 1,
						);
					},
				}),
			);
		}
	};

	const isHasSizes = useMemo(
		() => switchAttributes.some(item => item.name === 'Размер производителя'),
		[switchAttributes],
	);

	return (
		<div className={styles.config}>
			{switchAttributes.map(({ view_type, name, options = [], sizes }) => {
				if (!options || !options.length > 0) {
					return null;
				}
				const activeImageName = options.find(option => option.active)?.name ?? null;
				const conditionImage = view_type === 'images' && activeImageName && !isLook;

				if (conditionImage && setColorState) {
					setColorState(activeImageName?.toLowerCase());
				}

				if (activeImageName && name === 'Основной цвет' && setActiveImageName) {
					setActiveImageName(activeImageName);
				}

				return (
					<Fragment key={name}>
						{isLook && !isHasSizes && isModal && (
							<div
								className={clsx(styles.propertySize, isModal && isLook && styles.propertyLookModal)}
							>
								<div className={styles.propertySizeNameBlock}>
									<div className={styles.propertyName}>Размер:</div>
								</div>
								<div className={styles.propertySizeBlock}>
									<div className={styles.withoutSize}>Без размера</div>
								</div>
							</div>
						)}
						{conditionImage && (
							<dl className={clsx(styles.property, styles.noWrap)}>
								<dt className={styles.propertyName}>{name === 'Основной цвет' ? 'Цвет' : name}:</dt>
								<dd>{activeImageName}</dd>
							</dl>
						)}

						{view_type === 'images' &&
							!isLook &&
							(isModal ? (
								<ProductOptionsSliderModal
									isLook={isLook}
									setCurrentSlug={setCurrentSlug}
									options={options}
									onLoadingError={onLoadingError}
								/>
							) : (
								<ProductOptionsSlider
									options={options}
									isLook={isLook}
									setCurrentSlug={setCurrentSlug}
									onLoadingError={onLoadingError}
								/>
							))}

						{view_type === 'radio' && (
							<div
								className={clsx(styles.propertySize, isModal && isLook && styles.propertyLookModal)}
							>
								<div className={styles.propertySizeNameBlock}>
									<div className={styles.propertyName}>
										{name === 'Размер производителя' ? 'Размер' : name}:
									</div>
									{sizes && (
										<div
											className={clsx(
												{ [styles.noShow]: noShowSizeTable },
												isLook && styles.sizeTableLook,
												styles.propertySizeTable,
											)}
										>
											<SizeModal data={sizes} />
										</div>
									)}
								</div>
								<div className={styles.propertySizeBlock}>
									<ProductOptions
										isLook={isLook}
										isModal={isModal}
										index={index}
										options={options}
										setCurrentSlug={setCurrentSlug}
										setSizeChosenModal={setSizeChosenModal}
										setSizeLookModal={setSizeLookItem}
										currentSize={currentSize}
										setCurrentSize={setCurrentSize}
										setIsOpenSize={setIsOpenSize}
										isOpenSize={isOpenSize}
										productInfo={productInfo}
										addToCart={putIntoCart}
										addToWishlist={putIntoWishlist}
										setActualPrice={setActualPrice}
										sizes={sizes}
										noShowSizeTable={noShowSizeTable}
										actualPrice={actualPrice}
										sizeChoosenModalState={sizeChoosenModalState}
									/>
								</div>
								{sizes && (
									<div
										className={clsx(
											{ [styles.noShow]: noShowSizeTable },
											isLook && styles.sizeTableLook,
											styles.propertySizeTableMobile,
										)}
									>
										<SizeModal data={sizes} />
									</div>
								)}
							</div>
						)}
					</Fragment>
				);
			})}
			{isProductPage && children}
			{!noBucketButton && (
				<div className={styles.controls}>
					<div className={styles.btnContainer}>
						<AddToCart
							inStock={inStock}
							productInfo={productInfo}
							disabled={isModal ? !sizeChosenModal : false}
							productId={productIdForCartBtn}
							buttonProps={{
								classNames: { general: styles.button },
							}}
							currentSize={currentSize}
							setIsOpenSize={setIsOpenSize}
							onSuccess={addToCart}
							isProductConfig
							ids={ids}
						/>
					</div>

					{!isModal && (
						<WishlistBtn
							product={{ ...data, external_id: currentSlug || slug }}
							border
							index={productInfo?.indexCard ?? 1}
							isProductConfig
							isHideSizes={!isHasSizes}
							currentSize={currentSize}
							ids={wishlistIds}
							pageName={productInfo?.pageName}
						/>
					)}
				</div>
			)}
		</div>
	);
};

export default ProductConfig;
